import { Fragment } from 'react';

import cx from 'classnames';

import { formatDate } from '~/util/dateUtils';
import {
  getFormattedWeekdays,
  getSelectedYears,
  getFormattedMonthsForYear,
  getExactDates,
  WeddingDateView,
} from '~/util/weddingDates';

import styles from './expandedWeddingDates.module.less';

const Bullet = () => {
  return <span className={styles.bullet} role="presentation" />;
};

/**
 * Displays the flexible dates (months/years) from an inquiry to either
 * a couple of a vendor.  It is the caller of this component's responsibility
 * to verify that the inquiry is indeed a flexible date inquiry by calling
 * hasExpandedDates and hasFlexibleDates.
 */
export const FlexibleDateContent = ({ weddingDates }: { weddingDates: WeddingDateView | null }) => {
  const formattedWeekdays = getFormattedWeekdays(weddingDates);
  const selectedYears = getSelectedYears(weddingDates);

  return (
    <Fragment>
      {formattedWeekdays && (
        <dd className={styles.listItem} data-testid="flex-weekdays">
          <Bullet />
          <div>{formattedWeekdays}</div>
        </dd>
      )}
      {selectedYears.map((year) => {
        return (
          <dd className={styles.listItem} key={year} data-testid={`flex-dates-${year}`}>
            <Bullet />
            <div>
              <span className={styles.yearLabel}>{year}</span>:{' '}
              {getFormattedMonthsForYear(weddingDates, year)}
            </div>
          </dd>
        );
      })}
    </Fragment>
  );
};

/**
 * Displays the flexible dates (specific dates) from an inquiry to either
 * a couple of a vendor.  It is the caller of this component's responsibility
 * to verify that the inquiry is indeed a flexible date inquiry by calling
 * hasExpandedDates and hasExactDates.
 */
export const ExactDateContent = ({ weddingDates }: { weddingDates: WeddingDateView | null }) => {
  const exactDates = getExactDates(weddingDates);

  if (exactDates.length === 0) {
    return <Fragment />;
  }
  if (exactDates.length > 1) {
    return (
      <Fragment>
        {getExactDates(weddingDates).map((d, index) => {
          return (
            <dd
              className={cx(styles.listItem, { [styles.preferredDate]: d.preferred })}
              key={`exact-day-${index}`}
            >
              <Bullet />
              <div>
                {formatDate(d.date, 'MMMM D, YYYY')}
                {d.preferred && <Fragment> (preferred)</Fragment>}
              </div>
            </dd>
          );
        })}
      </Fragment>
    );
  }

  const date = exactDates[0];
  return <div>{formatDate(date.date, 'MMMM D, YYYY')}</div>;
};
